import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Loader2, Mail, Send } from "lucide-react";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function getOspiteName(fascicolo) {
  const anagrafica = fascicolo?.anagrafica || {};
  return `${anagrafica.nome || ""} ${anagrafica.cognome || ""}`.trim() || "ospite";
}

function getPrimaryFamiliare(fascicolo) {
  const contatti = fascicolo?.anagrafica?.contatti_familiari || [];
  return contatti.find((contatto) => contatto?.email) || contatti[0] || {};
}

function defaultInvioMessage(familiare, ospite) {
  return (
    `Gentile ${familiare || "familiare"},\n\n` +
    `le inviamo il link per completare la procedura online relativa a ${ospite}.\n\n` +
    `Dal link potra' firmare il contratto di ingresso e caricare la documentazione richiesta direttamente in Affidya.\n\n` +
    `Cordiali saluti`
  );
}

function defaultDocumentiMessage(familiare, ospite) {
  return (
    `Gentile ${familiare || "familiare"},\n\n` +
    `le inviamo il link per caricare la documentazione richiesta per ${ospite} direttamente in Affidya.\n\n` +
    `Cordiali saluti`
  );
}

export default function RichiestaDocumentiPreIngressoDialog({
  open,
  onOpenChange,
  fascicolo,
  fascicoloId,
  token,
  preIngressoCard,
  onSent,
}) {
  const ospiteNome = useMemo(() => getOspiteName(fascicolo), [fascicolo]);
  const familiare = useMemo(() => getPrimaryFamiliare(fascicolo), [fascicolo]);
  const contrattoFirmato = !!preIngressoCard?.contratto?.firmato;
  const [catalog, setCatalog] = useState([]);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [invio, setInvio] = useState({ email: "", oggetto: "", messaggio: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    let ignore = false;
    axios
      .get(`${API_URL}/api/preingresso/catalog`)
      .then(({ data }) => {
        if (ignore) return;
        const alreadyRequested = (preIngressoCard?.requested_documents || []).map((doc) => doc.key).filter(Boolean);
        setCatalog(data.documents || []);
        setSelectedDocs(alreadyRequested.length > 0 ? alreadyRequested : data.defaults || []);
      })
      .catch(() => {
        if (!ignore) {
          setCatalog([]);
          setSelectedDocs([]);
        }
      });

    setInvio({
      email: familiare.email || "",
      oggetto: contrattoFirmato
        ? `Documenti richiesti - ${ospiteNome}`
        : `Contratto di ingresso e documenti richiesti - ${ospiteNome}`,
      messaggio: contrattoFirmato
        ? defaultDocumentiMessage(familiare.nome, ospiteNome)
        : defaultInvioMessage(familiare.nome, ospiteNome),
    });

    return () => {
      ignore = true;
    };
  }, [open, contrattoFirmato, familiare.email, familiare.nome, ospiteNome, preIngressoCard?.requested_documents]);

  const toggleDoc = (key) => {
    setSelectedDocs((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
    );
  };

  const handleInvia = async () => {
    if (!invio.email.trim()) {
      toast.error("Email destinatario obbligatoria");
      return;
    }
    if (selectedDocs.length === 0 && contrattoFirmato) {
      toast.error("Seleziona almeno un documento da richiedere");
      return;
    }

    setSending(true);
    try {
      await axios.post(
        `${API_URL}/api/fascicoli-assistenziali/${fascicoloId}/pre-ingresso/invia`,
        {
          email: invio.email.trim(),
          oggetto: invio.oggetto.trim(),
          messaggio: invio.messaggio.trim(),
          requested_documents: selectedDocs,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Link inviato al familiare");
      onOpenChange(false);
      if (onSent) await onSent();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio richiesta");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-emerald-600" />
            Richiedi documenti al familiare
          </DialogTitle>
          <DialogDescription>
            {contrattoFirmato
              ? "Invia un messaggio con link sicuro per caricare i documenti nel fascicolo."
              : "Invia un messaggio con link sicuro per firmare il contratto e caricare i documenti nel fascicolo."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 py-2">
          <div className="lg:col-span-2 space-y-4">
            <div className="space-y-1.5">
              <Label>Destinatario *</Label>
              <Input
                type="email"
                value={invio.email}
                onChange={(e) => setInvio((prev) => ({ ...prev, email: e.target.value }))}
                placeholder="familiare@email.it"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Oggetto</Label>
              <Input
                value={invio.oggetto}
                onChange={(e) => setInvio((prev) => ({ ...prev, oggetto: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex justify-between items-center">
                <Label>Messaggio</Label>
                <Button
                  type="button"
                  variant="link"
                  className="h-auto p-0 text-xs"
                  onClick={() =>
                    setInvio((prev) => ({
                      ...prev,
                      messaggio: contrattoFirmato
                        ? defaultDocumentiMessage(familiare.nome, ospiteNome)
                        : defaultInvioMessage(familiare.nome, ospiteNome),
                    }))
                  }
                >
                  Ripristina testo predefinito
                </Button>
              </div>
              <Textarea
                rows={8}
                value={invio.messaggio}
                onChange={(e) => setInvio((prev) => ({ ...prev, messaggio: e.target.value }))}
              />
            </div>
            {!contrattoFirmato && (
              <div className="flex items-center gap-3 p-3 rounded-lg border bg-slate-50">
                <FileText className="w-8 h-8 text-red-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">Contratto di ingresso - {ospiteNome}.pdf</p>
                  <span className="text-xs text-emerald-600 font-medium">Allegato automaticamente</span>
                </div>
              </div>
            )}
            <div>
              <Label className="mb-2 block">
                Documenti da richiedere{!contrattoFirmato ? " (opzionale)" : ""}
              </Label>
              {!contrattoFirmato && (
                <p className="text-xs text-slate-500 mb-2">
                  Puoi deselezionare tutti i documenti se vuoi inviare solo il contratto da firmare.
                </p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 border rounded-lg p-3">
                {catalog.map((doc) => (
                  <label key={doc.key} className="flex items-start gap-2 text-sm cursor-pointer py-1">
                    <Checkbox
                      checked={selectedDocs.includes(doc.key)}
                      onCheckedChange={() => toggleDoc(doc.key)}
                      className="mt-0.5"
                    />
                    <span className="leading-snug">{doc.label}</span>
                  </label>
                ))}
                {catalog.length === 0 && (
                  <p className="text-sm text-slate-500">Catalogo documenti non disponibile.</p>
                )}
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm">
            <p className="font-semibold text-emerald-900 mb-3">Cosa succede dopo l&apos;invio?</p>
            <ol className="space-y-2 text-emerald-800 list-decimal list-inside text-xs">
              <li>Il familiare riceve email e link sicuro</li>
              <li>{contrattoFirmato ? "Il contratto gia' firmato non viene richiesto di nuovo" : "Il contratto viene allegato e richiesto in firma"}</li>
              <li>I documenti caricati entrano nel tab Documenti</li>
              <li>La checklist documenti si aggiorna automaticamente</li>
            </ol>
            {preIngressoCard?.portal_link && (
              <div className="mt-4 rounded-lg bg-white/70 border border-emerald-100 p-3">
                <p className="text-xs font-semibold text-emerald-900 mb-1">Link attuale</p>
                <p className="text-xs text-emerald-800 break-all">{preIngressoCard.portal_link}</p>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
            Annulla
          </Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleInvia} disabled={sending}>
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
            Invia link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
